// Polling clock — single utility for visibility-aware, backoff-aware polling.
//
// Pauses when document is hidden, resumes (and fires immediately) on visible.
// Pauses when navigator is offline, resumes on online.
// Backs off exponentially on errors (esp. 429), capped at maxIntervalMs.
// Adds jitter so multiple tabs/components don't sync.
//
// Use via createPollingClock(...) for plain JS, or usePollingClock(...) hook.

import { useEffect, useRef } from 'react';

const DEFAULT_MIN_INTERVAL_MS = 4000;
const DEFAULT_MAX_INTERVAL_MS = 60_000;
const DEFAULT_BACKOFF_FACTOR = 2;
const DEFAULT_JITTER_RATIO = 0.2;

export function createPollingClock(tick, options = {}) {
  const minIntervalMs = options.minIntervalMs ?? DEFAULT_MIN_INTERVAL_MS;
  const maxIntervalMs = options.maxIntervalMs ?? DEFAULT_MAX_INTERVAL_MS;
  const backoffFactor = options.backoffFactor ?? DEFAULT_BACKOFF_FACTOR;
  const jitterRatio = options.jitterRatio ?? DEFAULT_JITTER_RATIO;
  const fireOnVisible = options.fireOnVisible !== false;
  const fireImmediately = options.fireImmediately !== false;

  let currentInterval = minIntervalMs;
  let timeoutId = null;
  let stopped = false;
  let inFlight = false;

  function scheduleNext() {
    if (stopped) return;
    if (timeoutId !== null) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;

    const jitter = currentInterval * jitterRatio * (Math.random() - 0.5);
    const delay = Math.max(minIntervalMs / 2, Math.round(currentInterval + jitter));
    timeoutId = globalThis.setTimeout(runTick, delay);
  }

  async function runTick() {
    timeoutId = null;
    if (stopped || inFlight) {
      scheduleNext();
      return;
    }
    inFlight = true;
    try {
      await tick();
      currentInterval = minIntervalMs;
    } catch (err) {
      const status = err?.status ?? err?.code;
      const isRateLimited = status === 429 || err?.code === 'RATE_LIMITED';
      const factor = isRateLimited ? backoffFactor * 2 : backoffFactor;
      currentInterval = Math.min(maxIntervalMs, Math.max(minIntervalMs, currentInterval * factor));
    } finally {
      inFlight = false;
      scheduleNext();
    }
  }

  function onVisibilityChange() {
    if (typeof document === 'undefined') return;
    if (document.visibilityState === 'visible') {
      currentInterval = minIntervalMs;
      if (fireOnVisible && !inFlight && timeoutId === null) {
        runTick();
      } else {
        scheduleNext();
      }
    } else if (timeoutId !== null) {
      globalThis.clearTimeout(timeoutId);
      timeoutId = null;
    }
  }

  function onOnline() {
    currentInterval = minIntervalMs;
    if (!inFlight && timeoutId === null) runTick();
  }

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibilityChange);
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('online', onOnline);
  }

  if (fireImmediately && (typeof document === 'undefined' || document.visibilityState !== 'hidden')) {
    runTick();
  } else {
    scheduleNext();
  }

  return function stop() {
    stopped = true;
    if (timeoutId !== null) {
      globalThis.clearTimeout(timeoutId);
      timeoutId = null;
    }
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', onOnline);
    }
  };
}

export function usePollingClock(tick, options = {}) {
  const tickRef = useRef(tick);
  tickRef.current = tick;

  const enabled = options.enabled !== false;
  const minIntervalMs = options.minIntervalMs;
  const maxIntervalMs = options.maxIntervalMs;
  const fireImmediately = options.fireImmediately;

  useEffect(() => {
    if (!enabled) return undefined;
    const stop = createPollingClock(() => tickRef.current?.(), {
      minIntervalMs,
      maxIntervalMs,
      fireImmediately,
    });
    return stop;
  }, [enabled, minIntervalMs, maxIntervalMs, fireImmediately]);
}
